import { CONSTANTS } from "../data";

/**
 * Auto-generates a unique seed based on current game state.
 * The seed encodes level, recipes, coins, XP, prestige, and other key state
 * into a compact string that can be shared and parsed later.
 * 
 * Format: CA-{timestamp}-{stateHash}-{random}
 * Where stateHash contains base36-encoded game state values
 * 
 * @param gameState - Current game state to encode
 * @returns Encoded seed string
 */
export const generateAutoSeed = (gameState: {
  level: number;
  discoveredRecipes: string[];
  coins: number;
  xp: number;
  prestigeLevel: number;
  activeSeason: string | null;
  inventory?: Record<string, number>;
  reputation?: number;
}): string => {
  try {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * CONSTANTS.SEED_GENERATION.RANDOM_RANGE);
    
    // Create a comprehensive state representation
    const stateData = {
      level: Math.max(1, gameState.level),
      recipes: gameState.discoveredRecipes.length,
      coins: Math.max(0, Math.floor(gameState.coins / CONSTANTS.SEED_GENERATION.COINS_DIVISOR)),
      xp: Math.max(0, Math.floor(gameState.xp / CONSTANTS.SEED_GENERATION.XP_DIVISOR)),
      prestige: Math.max(0, gameState.prestigeLevel),
      season: gameState.activeSeason ? gameState.activeSeason.charAt(0) : 'N',
      reputation: gameState.reputation ? Math.floor(gameState.reputation / 10) : 5
    };
    
    // Create a compact hash of the state using base36 encoding
    const stateHash = [
      stateData.level.toString(36),
      stateData.recipes.toString(36),
      stateData.coins.toString(36),
      stateData.xp.toString(36),
      stateData.prestige.toString(36),
      stateData.season,
      stateData.reputation.toString(36)
    ].join('');
    
    // Combine timestamp, state hash, and random component
    const seedComponents = [
      CONSTANTS.SEED_GENERATION.PREFIX,
      timestamp.toString(36),
      stateHash,
      random.toString(36)
    ];
    
    return seedComponents.join('-');
  } catch (error) {
    console.error("Failed to generate seed:", error);
    // Return a basic fallback seed
    return `${CONSTANTS.SEED_GENERATION.PREFIX}-${Date.now().toString(36)}-basic-${Math.floor(Math.random() * 1000).toString(36)}`;
  }
};

/**
 * Parses a seed string and returns game state data.
 * Supports both new auto-generated seeds and legacy manual seeds.
 * 
 * @param seed - Seed string to parse
 * @param seasonalItem - Current seasonal item for legacy seeds
 * @returns Parsed game state data or default values on failure
 */
export const parseSeededData = (seed: string, seasonalItem: string = '') => {
  if (!seed || typeof seed !== 'string') {
    console.warn("Invalid seed provided:", seed);
    return getDefaultSeedData(seasonalItem);
  }

  try {
    const trimmedSeed = seed.trim();
    
    // Check if it's a new auto-generated seed
    if (trimmedSeed.startsWith(CONSTANTS.SEED_GENERATION.PREFIX + '-')) {
      return parseAutoGeneratedSeed(trimmedSeed);
    }
    
    // Handle legacy manual seeds (CB-level-recipes format)
    if (trimmedSeed.startsWith('CB-')) {
      return parseLegacySeed(trimmedSeed, seasonalItem);
    }
    
    // Handle raw seeds without prefix
    return parseRawSeed(trimmedSeed, seasonalItem);
  } catch (error) {
    console.error("Failed to parse seed:", error);
    return getDefaultSeedData(seasonalItem);
  }
};

/**
 * Parses new auto-generated seeds with comprehensive state encoding.
 * These seeds contain timestamp, state hash, and random components.
 * 
 * @param seed - Auto-generated seed string
 * @returns Parsed game state data
 */
const parseAutoGeneratedSeed = (seed: string) => {
  try {
    const parts = seed.split('-');
    if (parts.length < 4) {
      throw new Error("Invalid auto-generated seed format");
    }
    
    const [prefix, timestampStr, stateHashStr, randomStr] = parts;
    
    if (prefix !== CONSTANTS.SEED_GENERATION.PREFIX) {
      throw new Error("Invalid seed prefix");
    }
    
    const timestamp = parseInt(timestampStr, 36);
    const random = parseInt(randomStr, 36);
    
    if (isNaN(timestamp) || isNaN(random)) {
      throw new Error("Invalid seed components");
    }
    
    // Parse state hash - handle variable length components
    const stateComponents = [];
    let currentPos = 0;
    const maxComponentLength = 3; // Maximum expected length for base36 numbers
    
    while (currentPos < stateHashStr.length) {
      let componentLength = 1;
      // Try to find the optimal component length
      while (componentLength <= maxComponentLength && currentPos + componentLength <= stateHashStr.length) {
        const component = stateHashStr.substr(currentPos, componentLength);
        const parsed = parseInt(component, 36);
        if (!isNaN(parsed)) {
          componentLength++;
        } else {
          break;
        }
      }
      componentLength--; // Go back to last valid length
      
      if (componentLength > 0) {
        stateComponents.push(stateHashStr.substr(currentPos, componentLength));
        currentPos += componentLength;
      } else {
        // If we can't parse, take single character
        stateComponents.push(stateHashStr.charAt(currentPos));
        currentPos++;
      }
    }
    
    // Extract state information with defaults
    const level = stateComponents[0] ? Math.max(1, parseInt(stateComponents[0], 36)) : 1;
    const recipeCount = stateComponents[1] ? Math.max(0, parseInt(stateComponents[1], 36)) : 0;
    const coins = stateComponents[2] ? Math.max(CONSTANTS.STARTING_COINS, parseInt(stateComponents[2], 36) * CONSTANTS.SEED_GENERATION.COINS_DIVISOR) : CONSTANTS.STARTING_COINS;
    const xp = stateComponents[3] ? Math.max(0, parseInt(stateComponents[3], 36) * CONSTANTS.SEED_GENERATION.XP_DIVISOR) : 0;
    const prestigeLevel = stateComponents[4] ? Math.max(0, parseInt(stateComponents[4], 36)) : 0;
    const seasonCode = stateComponents[5] || 'N';
    const reputation = stateComponents[6] ? Math.max(0, Math.min(100, parseInt(stateComponents[6], 36) * 10)) : CONSTANTS.REPUTATION_CONFIG.INITIAL_REPUTATION;
    
    // Generate a deterministic set of recipes based on the seed
    const recipes = generateDeterministicRecipes(recipeCount, timestamp + random);
    
    // Generate deterministic inventory based on level and recipes
    const inventory = generateDeterministicInventory(level, recipes);
    
    return {
      level,
      recipes,
      coins,
      xp,
      prestigeLevel,
      reputation,
      inventory,
      timestamp,
      isAutoGenerated: true,
      valid: true
    };
  } catch (error) {
    console.error("Failed to parse auto-generated seed:", error);
    throw error;
  }
};

/**
 * Parses legacy manual seeds in CB-level-recipes format.
 * These are simpler seeds that only encode level and recipe list.
 * 
 * @param seed - Legacy seed string
 * @param seasonalItem - Current seasonal item
 * @returns Parsed game state data
 */
const parseLegacySeed = (seed: string, seasonalItem: string) => {
  try {
    const parts = seed.replace(/^CB-/, "").split("-");
    const level = Math.max(1, parseInt(parts[0]) || 1);
    const recipes = parts.slice(1).filter(r => r && r !== '' && CONSTANTS.RECIPES[r]);
    
    // Generate inventory based on level for legacy seeds
    const inventory = generateDeterministicInventory(level, recipes);
    
    return {
      level,
      recipes,
      seasonalItem,
      coins: CONSTANTS.STARTING_COINS + (level - 1) * 100, // Scale coins with level
      xp: CONSTANTS.LEVEL_XP_THRESHOLDS[level] || 0,
      prestigeLevel: 0,
      reputation: CONSTANTS.REPUTATION_CONFIG.INITIAL_REPUTATION,
      inventory,
      isAutoGenerated: false,
      valid: true
    };
  } catch (error) {
    console.error("Failed to parse legacy seed:", error);
    throw error;
  }
};

/**
 * Parses raw seeds without any prefix.
 * Uses character codes to generate deterministic but basic game state.
 * 
 * @param seed - Raw seed string
 * @param seasonalItem - Current seasonal item
 * @returns Parsed game state data
 */
const parseRawSeed = (seed: string, seasonalItem: string) => {
  try {
    // Try to extract meaningful data from raw seed
    const seedValue = seed.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    const level = Math.max(1, (seedValue % 10) + 1);
    const recipeCount = Math.max(0, (seedValue % 20));
    
    const recipes = generateDeterministicRecipes(recipeCount, seedValue);
    const inventory = generateDeterministicInventory(level, recipes);
    
    return {
      level,
      recipes,
      seasonalItem,
      coins: CONSTANTS.STARTING_COINS + (level - 1) * 50,
      xp: CONSTANTS.LEVEL_XP_THRESHOLDS[level] || 0,
      prestigeLevel: 0,
      reputation: CONSTANTS.REPUTATION_CONFIG.INITIAL_REPUTATION,
      inventory,
      rawSeed: seed,
      isAutoGenerated: false,
      valid: true
    };
  } catch (error) {
    console.error("Failed to parse raw seed:", error);
    throw error;
  }
};

/**
 * Generates a deterministic set of recipes using a linear congruential generator.
 * This ensures that the same seed will always produce the same recipe set.
 * 
 * @param count - Target number of recipes to generate
 * @param seedValue - Seed value for deterministic generation
 * @returns Array of recipe IDs
 */
const generateDeterministicRecipes = (count: number, seedValue: number): string[] => {
  const allRecipes = Object.keys(CONSTANTS.RECIPES);
  const recipes: string[] = [];
  
  // Always include basic recipes
  const basicRecipes = ['beans_grinder', 'ground_coffee_espresso_machine', 'espresso_water', 'espresso_milk'];
  basicRecipes.forEach(recipe => {
    if (CONSTANTS.RECIPES[recipe] && !recipes.includes(recipe)) {
      recipes.push(recipe);
    }
  });
  
  // Use seed value to create deterministic selection for additional recipes
  let currentSeed = seedValue;
  const targetCount = Math.min(count, allRecipes.length);
  
  while (recipes.length < targetCount) {
    currentSeed = (currentSeed * CONSTANTS.SEED_GENERATION.MULTIPLIER + CONSTANTS.SEED_GENERATION.INCREMENT) % CONSTANTS.SEED_GENERATION.MODULUS;
    const index = currentSeed % allRecipes.length;
    const recipe = allRecipes[index];
    
    if (!recipes.includes(recipe)) {
      recipes.push(recipe);
    }
    
    // Prevent infinite loop
    if (recipes.length >= allRecipes.length) break;
  }
  
  return recipes;
};

/**
 * Generates deterministic inventory based on level and discovered recipes.
 * Higher levels get more base ingredients, and recipe results are added to inventory.
 * 
 * @param level - Player level
 * @param recipes - Array of discovered recipe IDs
 * @returns Inventory object with item quantities
 */
const generateDeterministicInventory = (level: number, recipes: string[]): Record<string, number> => {
  const inventory = { ...CONSTANTS.STARTING_INVENTORY };
  
  // Add items based on level
  const baseMultiplier = Math.max(1, level);
  inventory.water = Math.max(inventory.water, baseMultiplier * 5);
  inventory.beans = Math.max(inventory.beans, baseMultiplier * 5);
  inventory.milk = Math.max(inventory.milk, baseMultiplier * 5);
  inventory.sugar = Math.max(inventory.sugar, baseMultiplier * 3);
  inventory.ice = Math.max(inventory.ice, baseMultiplier * 3);
  
  // Add level-based unlocks
  if (level >= 2) inventory.cocoa_powder = Math.max(inventory.cocoa_powder || 0, 3);
  if (level >= 3) inventory.vanilla = Math.max(inventory.vanilla || 0, 3);
  if (level >= 4) inventory.shaker = 1;
  if (level >= 5) inventory.ice_cream = Math.max(inventory.ice_cream || 0, 2);
  if (level >= 6) inventory.turkish_grinder = 1;
  
  // Add recipe results to inventory
  recipes.forEach(recipeId => {
    const recipe = CONSTANTS.RECIPES[recipeId];
    if (recipe && recipe.result) {
      const currentAmount = inventory[recipe.result.id] || 0;
      inventory[recipe.result.id] = Math.max(currentAmount, 1);
    }
  });
  
  return inventory;
};

/**
 * Returns default seed data for fallback cases when seed parsing fails.
 * 
 * @param seasonalItem - Current seasonal item
 * @returns Default game state data
 */
const getDefaultSeedData = (seasonalItem: string) => {
  return {
    level: 1,
    recipes: ['beans_grinder', 'ground_coffee_espresso_machine'],
    seasonalItem,
    coins: CONSTANTS.STARTING_COINS,
    xp: 0,
    prestigeLevel: 0,
    reputation: CONSTANTS.REPUTATION_CONFIG.INITIAL_REPUTATION,
    inventory: { ...CONSTANTS.STARTING_INVENTORY },
    isAutoGenerated: false,
    valid: false
  };
};

/**
 * Legacy function for backward compatibility with old seed format.
 * 
 * @param discoveredRecipes - Array of discovered recipe IDs
 * @param level - Player level
 * @returns Legacy seed string
 */
export const generateSeedCode = (discoveredRecipes: string[], level: number): string => {
  return `CB-${level}-${discoveredRecipes.join("-")}`;
};
